import { useState, useEffect, useCallback, useRef } from 'react';
import * as MixpanelAudience from 'react/infra/mixpanel/MixpanelAudience';
import { VIEW_TYPES_NAMES, ALL_INTERESTS, ALL_CATEGORIES } from '../constants';
import { getInterestType } from '../utils/audienceInsightsUtils';

const useAudienceUI = ({
  selectedTab,
  tabsNames,
  interestTypes,
  audienceChannel,
  abiPermissions,
  interests,
  setInterests,
  resetInterests,
  topic,
  setTopic,
  targetsCommon,
  targets,
  $state,
  $scope,
  publishAllInterestsToView,
  getPromiseOfTestedInterestsByType,
  sortType,
  setSortType,
}) => {
  const isCategoriesLikeTab = [tabsNames?.categories, tabsNames?.purchaseAffinities].includes(selectedTab?.value);
  const [viewType, setViewType] = useState(VIEW_TYPES_NAMES.chart);
  const [lastViewType, setLastViewType] = useState(VIEW_TYPES_NAMES.chart);
  const [title, setTitle] = useState(null);
  const [interestType, setInterestType] = useState(isCategoriesLikeTab ? 'categories' : interestTypes[0]?.value);
  const [showTargets, setShowTargets] = useState(false);
  const [queryData, setQueryData] = useState({ selected: [], dataArray: [], columns: [] });
  const prevInterestTypeRef = useRef(interestType);
  const savedInterestsStateRef = useRef(null);

  // Synchronously update interestType and related state when selectedTab changes during render.
  // This prevents the "flash" of previous tab content before useEffect runs.
  const [prevSelectedTab, setPrevSelectedTab] = useState(selectedTab);
  if (selectedTab !== prevSelectedTab) {
    const newType = getInterestType(selectedTab, tabsNames, interestTypes);
    const isGoingToCategories = newType === 'categories';
    const isComingFromCategories = [tabsNames?.categories, tabsNames?.purchaseAffinities].includes(
      prevSelectedTab?.value
    );

    setPrevSelectedTab(selectedTab);
    setInterestType(newType);
    resetInterests();

    if (isGoingToCategories) {
      // Save current topic/title before switching to categories
      savedInterestsStateRef.current = { topic, title };
      setViewType(VIEW_TYPES_NAMES.table);
      setTopic(null);
      setTitle(ALL_CATEGORIES);
    } else if (isComingFromCategories && savedInterestsStateRef.current) {
      // Restore saved topic/title when coming back from categories
      setViewType(lastViewType);
      setTopic(savedInterestsStateRef.current.topic);
      setTitle(savedInterestsStateRef.current.title);
    } else {
      // Default behavior for other tab switches
      setTopic({ id: ALL_INTERESTS });
      setTitle(ALL_INTERESTS);
    }
  }

  const resetSelection = useCallback(() => {
    if (queryData?.selected?.length) {
      setQueryData((prev) => ({
        ...prev,
        selected: [],
        dataArray: prev.dataArray.map((row) => ({ ...row, selected: false })),
      }));
      if (interests) setInterests((prev) => prev.map((row) => ({ ...row, selected: false })));
    }
  }, [queryData, interests, setInterests]);

  const doPublishAllInterestsToView = useCallback(
    (updateQueryObj = false, type = interestType, topicId = topic?.id || ALL_INTERESTS) =>
      publishAllInterestsToView({
        updateQueryObj,
        type,
        topicId,
        lastViewType: type === 'categories' ? VIEW_TYPES_NAMES.table : lastViewType,
        setViewType,
        setQueryData,
        resetSelection,
        getPromiseOfTestedInterestsByType,
      }),
    [
      interestType,
      topic,
      lastViewType,
      isCategoriesLikeTab,
      publishAllInterestsToView,
      resetSelection,
      getPromiseOfTestedInterestsByType,
    ]
  );

  // Tab change effect
  useEffect(() => {
    if (selectedTab?.value === 'demographics') return;

    const newType = getInterestType(selectedTab, tabsNames, interestTypes);
    doPublishAllInterestsToView(true, newType, newType === 'categories' ? ALL_INTERESTS : topic?.id || ALL_INTERESTS);
  }, [selectedTab, tabsNames, interestTypes]);

  // Show targets effect
  useEffect(() => {
    setShowTargets(interestType !== 'websites' && abiPermissions.hasPermission('activate targets'));
  }, [interestType, abiPermissions]);

  // Load targets effect
  useEffect(() => {
    if (showTargets && targetsCommon && $state && $scope && prevInterestTypeRef.current !== interestType) {
      $scope.tab = interestType;
      $scope.targets = targets;
      targetsCommon.getTargets({ scope: $scope, $state, tab: interestType });
    }
    prevInterestTypeRef.current = interestType;
  }, [interestType, showTargets, targetsCommon, targets, $state, $scope]);

  // Mixpanel tracking
  useEffect(() => {
    MixpanelAudience.trackDemographicsInterestsView(sortType, 'interests', audienceChannel);
  }, [sortType, audienceChannel]);

  const handleInterestTypeChange = useCallback(
    (type) => {
      setInterestType(type);
      resetInterests();
      resetSelection();
      MixpanelAudience.trackInterestTypeChanged(type, audienceChannel);
      doPublishAllInterestsToView(true, type, topic?.id || ALL_INTERESTS);
    },
    [audienceChannel, resetSelection, doPublishAllInterestsToView, resetInterests, topic]
  );

  const handleViewTypeChange = useCallback(
    (type) => {
      if (interestType === 'categories') return;
      setLastViewType(type);
      setViewType(type);
      resetSelection();
    },
    [interestType, resetSelection]
  );

  const handleSortTypeChange = useCallback(
    (type) => {
      MixpanelAudience.trackViewChange(type, 'audience interests', '', audienceChannel);
      MixpanelAudience.trackDemographicsInterestsView(type, 'interests', audienceChannel);
      setSortType(type);
      resetSelection();
      if (topic && interestType) doPublishAllInterestsToView(true);
    },
    [audienceChannel, resetSelection, topic, interestType, doPublishAllInterestsToView]
  );

  return {
    viewType,
    sortType,
    title,
    setTitle,
    interestType,
    showTargets,
    queryData,
    setQueryData,
    resetSelection,
    doPublishAllInterestsToView,
    handleInterestTypeChange,
    handleViewTypeChange,
    handleSortTypeChange,
  };
};

export default useAudienceUI;
